import { useMutation } from 'react-query'
import { SubmitHandler, useFormContext } from 'react-hook-form'
import { useCallback } from 'react'
import { Link } from 'react-router-dom'
import BackNavLink from 'core/components/navigation/BackNavLink'
import APIService from 'api/APIService'
import { AuthFormValue } from 'pages/auth/type'
import { ErrorMessage } from 'types/entity'
import { useUserProfile } from 'core/hooks/useDataHook'
import useNavigation from 'core/hooks/useNavigation'
import { ERROR_PASSWORD_NOT_JUST_WHITESPACE } from 'pages/account/profile/validate'
import AsyncButton from 'core/components/buttons/AsyncButton'
import SmartField from 'core/components/form/SmartField'
import Input from 'core/components/inputs/Input'
import FeatureIcon from 'core/components/icons/FeatureIcon'
import { setAuthTokenCookie } from 'utils/AppHelper'

const LoginPage = () => {
  const { search, replace } = useNavigation()
  const urlParams = new URLSearchParams(search)
  const redirect_url = urlParams.get('redirect_url')
  const gt = urlParams.get('gt') || null
  const { control, handleSubmit } = useFormContext<AuthFormValue>()
  const { setData } = useUserProfile()

  const { isLoading, isError, error, mutate } = useMutation(
    ['login'],
    APIService.users.login,
    {
      onSuccess: (user) => {
        setAuthTokenCookie(user.auth_token)
        setData(user)
        const newUrlParams = new URLSearchParams(search)
        newUrlParams.delete('redirect_url')
        const newSearch = newUrlParams.toString()

        if (user.received_invites?.length > 0 && user?.teams?.length === 0) {
          replace('/accept-invite')
          return
        }

        if (user.teams?.[0]?.roles?.[0]?.name === 'Reviewer') {
          replace('/teams/compliance')
          return
        }

        if (user.teams?.[0]?.roles?.length && !redirect_url) {
          replace('/teams')
          return
        }

        if (redirect_url === '/account/compliance') {
          replace(`/compliance${newSearch ? `?${newSearch}` : ''}`)
          return
        }

        replace(redirect_url ?? '/dashboard')
      }
    }
  )
  const onSubmit: SubmitHandler<AuthFormValue> = useCallback(
    ({ email, password }) =>
      mutate({
        email,
        password,
        gt,
        mixpanel_distinct_id: window?.mixpanel?.get_distinct_id?.()
      }),
    [gt, mutate]
  )

  return (
    <div
      data-title="login-screen"
      className="h-[88vh] flex justify-around items-center"
    >
      <div className="card bg-white mb-5 w-[500px]">
        <BackNavLink to="/auth/start" />
        <div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex justify-center">
              <FeatureIcon name="everlance" />
            </div>
            <div className="flex justify-center">
              <h3 className="pb-6" title="greeting">
                Welcome back!
              </h3>
            </div>
            <div className="mt-6 flex flex-col gap-4">
              <SmartField
                controllerProps={{
                  control,
                  name: 'email',
                  rules: {
                    required: 'Email is required',
                    validate: {
                      noBlank: (value) =>
                        value.trim().length > 0 ||
                        'Email cannot be just whitespace'
                    }
                  }
                }}
              >
                <Input
                  placeholder="email@email.com"
                  className="text-lg mb-2 w-full py-3.5"
                />
              </SmartField>

              <SmartField
                controllerProps={{
                  control,
                  name: 'password',
                  rules: {
                    required: 'Password is required',
                    validate: {
                      noBlank: (value) =>
                        value.trim().length > 0 ||
                        ERROR_PASSWORD_NOT_JUST_WHITESPACE
                    }
                  }
                }}
              >
                <Input
                  type="password"
                  placeholder="Enter your password"
                  autoFocus
                  className="text-lg mb-2 w-full py-3.5"
                />
              </SmartField>

              <AsyncButton
                type="submit"
                variant="primary"
                status={isLoading ? 'loading' : 'idle'}
                disabled={isLoading}
                title="submit-button"
                className="w-full"
              >
                Login
              </AsyncButton>
            </div>

            {isError && (
              <div>
                <p className="pt-2 text-danger">
                  {(error as ErrorMessage)?.response?.data?.message ??
                    'An unknown error occurred'}
                </p>
              </div>
            )}
          </form>
          <div className="flex justify-center mt-12">
            <Link to="/password_resets/new" className="select-none">
              Forgot Password?
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
